import {
  LOCAL_STORAGE_AUTH_LAST_ACTIVITY,
  LOCAL_STORAGE_AUTH_SESSION_ENDED_AT,
} from '../constants';

export const IDLE_TIMEOUT_MINUTES = 20;
export const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;
export const IDLE_WARNING_MS = 60 * 1000;

export const isIdle = (lastActivityAt: number, now: number): boolean =>
  now - lastActivityAt >= IDLE_TIMEOUT_MS;

// Storage can be blocked by the browser; the app then runs without the
// load-time check instead of failing.
const readTime = (key: string): number | null => {
  try {
    const stored = window.localStorage.getItem(key);

    if (stored === null) {
      return null;
    }

    const at = Number(stored);

    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
};

const writeTime = (key: string, at: number): void => {
  try {
    window.localStorage.setItem(key, String(at));
  } catch {
    // Storage is unavailable.
  }
};

export const readLastActivity = (): number | null =>
  readTime(LOCAL_STORAGE_AUTH_LAST_ACTIVITY);

export const writeLastActivity = (at: number): void => {
  writeTime(LOCAL_STORAGE_AUTH_LAST_ACTIVITY, at);
};

// A session that ended, or may not have ended at Identity, is stored as idle
// for twice the limit, so it stays stale when the clock moves back a little.
// Every page load then asks Identity for a sign-in newer than `now` until a
// sign-in completes.
export const markStoredSessionStale = (now: number = Date.now()): void => {
  writeLastActivity(now - 2 * IDLE_TIMEOUT_MS);
  writeTime(LOCAL_STORAGE_AUTH_SESSION_ENDED_AT, now);
};

// True when a last-activity time is stored and older than the idle limit. The
// login gates share this so they cannot drift.
export const isStoredSessionStale = (now: number = Date.now()): boolean => {
  const at = readLastActivity();

  return at !== null && isIdle(at, now);
};

// The OIDC max_age for the login of a stale session: the seconds since the
// session ended (its marked end, or its last activity when it was left idle).
// Identity reuses only a sign-in made after that moment; 0 when nothing is
// stored or the clock moved back, so Identity asks for a new sign-in.
export const maxAgeSinceStale = (now: number = Date.now()): number => {
  const endedAt = Math.max(
    readTime(LOCAL_STORAGE_AUTH_SESSION_ENDED_AT) ?? 0,
    readLastActivity() ?? 0
  );

  return endedAt === 0 ? 0 : Math.max(0, Math.floor((now - endedAt) / 1000));
};
